import React, {useEffect, useState} from 'react';
import {useParams, useLocation} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {isPast} from 'date-fns';

import {selectApp, selectWisbit} from 'store/selectors';

import {ROUTE, MODE} from 'containers/App/constants';

import {useError} from 'hooks/useError';

import {findQuery} from 'utils';

import {
  Header,
  ToggleButton,
  ConfirmMessage,
  WarningMessage,
  LinkButton,
  ToastMessage,
  ErrorMessage,
  Loading,
} from 'components';

import ArrowLeft from 'assets/icons/arrow-left.svg';

import {
  loadWisbit,
  loadWisbitTemplate,
  publishWisbit,
  deleteWisbit,
  saveWisbit,
} from './redux/thunks';

import {initiateWisbit, clearLoading} from './redux/actions';
import {validateComponent} from './utils';
import {FIELDS} from './constants';

import GeneralInformation from './GeneralInformation';
import Bits from './Bits';
import i18n from 'i18next';
import DownloadButton from './HeaderButtons/DownloadButton';
import DuplicateButton from './HeaderButtons/DuplicateButton';
import DeleteButton from './HeaderButtons/DeleteButton';
import {USER_TYPE} from 'containers/ManageUserPage/userType';
import request from 'api/requests/request';
import {convertWisbitToLessonDTO} from 'api/converters/wisbitConverters';
import {push} from 'connected-react-router';
import {downloadResult} from 'api/requests/wisbitRequests';

function WisbitPage() {
  const [showPublishMessage, setShowPublishMessage] = useState<boolean>(false);
  const [editorMode, setEditorMode] = useState<string>(MODE.VIEW);

  const {setValidationError, errorMessage} = useError();

  const {user} = useSelector(selectApp);
  const {showSavedMessage, loading, saving, deleted, ...wisbit} =
    useSelector(selectWisbit);

  const isContentCreator = user.role === USER_TYPE.CONTENT_CREATOR.serverAlias;

  const {
    published,
    id: wisbitId,
    bits,
    title,
    description,
    thumbnailId,
    date,
    tags,
  } = wisbit;

  const dispatch = useDispatch();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const {id, mode = MODE.VIEW} = useParams<any>();

  const template = findQuery(useLocation().search, 'template') || undefined;

  useEffect(() => {
    if (!id) {
      setEditorMode(MODE.CREATE);
    } else if (wisbitId) {
      setEditorMode(mode);
    } else {
      setEditorMode(MODE.VIEW);
    }
  }, [id, mode, wisbitId, bits]);

  useEffect(() => {
    if (!showSavedMessage) {
      return;
    }
    setValidationError(undefined);
  }, [showSavedMessage, setValidationError]);

  useEffect(() => {
    if (id && !deleted) {
      if (!wisbit || wisbit.id !== id) {
        dispatch(loadWisbit(id));
      } else if (loading && wisbit.template) {
        dispatch(clearLoading());
      }
    }
  }, [dispatch, id, wisbit, loading, deleted]);

  useEffect(() => {
    if (!id && !deleted) {
      if (template) {
        if (!loading) {
          dispatch(loadWisbitTemplate(template));
        } else if (!saving && !wisbitId) {
          dispatch(saveWisbit());
        }
      } else if (wisbitId) {
        dispatch(initiateWisbit());
      }
    }
  }, [dispatch, id, wisbitId, template, saving, loading, deleted]);

  useEffect(() => {
    if (bits && bits.length > 0) {
      setValidationError({
        field: FIELDS.BITS,
        message: '',
      });
    }
  }, [setValidationError, bits]);

  const onShowPublishMessage = () => {
    if (!showPublishMessage) {
      if (!wisbitId) {
        setValidationError({
          field: FIELDS.BITS,
          message: i18n.t('wisbitPage.index.validationErrors.id'),
        });
      } else if (!title) {
        setValidationError({
          field: FIELDS.TITLE,
          message: i18n.t('wisbitPage.index.validationErrors.title'),
        });
      } else if (!description) {
        setValidationError({
          field: FIELDS.DESC,
          message: i18n.t('wisbitPage.index.validationErrors.description'),
        });
      } else if (!thumbnailId) {
        setValidationError({
          field: FIELDS.THUMBNAIL,
          message: i18n.t('wisbitPage.index.validationErrors.thumbnail'),
        });
      } else if (!bits || bits.length < 1) {
        setValidationError({
          field: FIELDS.BITS,
          message: i18n.t('wisbitPage.index.validationErrors.bits'),
        });
      } else if (date && isPast(new Date(date))) {
        setValidationError({
          field: 'date',
          message: i18n.t('wisbitPage.index.validationErrors.date'),
        });
      } else if (!tags || tags.length < 1 || tags.length > 3) {
        setValidationError({
          field: FIELDS.TAGS,
          message: i18n.t('wisbitPage.index.validationErrors.tags'),
        });
      } else {
        let message = '';

        bits.forEach(({title, duration, components}) => {
          if (!title) {
            message += `<br />${i18n.t(
              'wisbitPage.index.validationErrors.perBitErrors.title'
            )}`;
          } else if (!duration || duration <= 0) {
            message += `<br /> ${i18n.t(
              'wisbitPage.index.validationErrors.perBitErrors.duration'
            )}`;
          } else if (components && components.length > 0) {
            components.forEach(component => {
              const location = `<br/>${title}:${component.type}`;

              validateComponent(component, () => {
                message += location;
              });
            });
          } else {
            message += `<br />${title}: ${i18n.t(
              'wisbitPage.index.validationErrors.perBitErrors.other'
            )}`;
          }
        });

        if (message) {
          setValidationError({
            field: FIELDS.BITS,
            message: `${i18n.t(
              'wisbitPage.index.validationErrors.perBitErrors.message'
            )} ${message}`,
          });
        } else {
          setShowPublishMessage(!showPublishMessage);
        }
      }
    } else {
      setShowPublishMessage(!showPublishMessage);
    }
  };

  const onPublish = () => {
    if (wisbitId) {
      dispatch(publishWisbit(wisbitId));
    }

    onShowPublishMessage();
  };

  const onDownload = async () => {
    try {
      if (wisbitId) {
        await downloadResult(wisbitId, user.id);
      }
    } catch {
      console.error('Download failed!');
    }
  };

  const onDelete = () => {
    if (id) {
      dispatch(deleteWisbit(id, !!published));
    }
  };

  /**
   * Duplicates wisbit without uploading new images.
   * Also, suffix is appended to the title of the wisbit.
   */
  const onDuplicate = async () => {
    const wisbitDTOdata = await convertWisbitToLessonDTO(dispatch, wisbit);

    // Append suffix to the title to indice that copy was made.
    const titleSuffix = i18n.t('wisbitPage.index.duplicate.copySuffix');
    wisbitDTOdata.title = `${wisbitDTOdata.title} - ${titleSuffix}`;

    request('/lesson', {method: 'POST', data: wisbitDTOdata})
      .then(d => dispatch(push(`/wisbit/${d.id}/edit`)))
      .catch(() => dispatch(push('/wisbit')));
  };

  if (loading) {
    return (
      <div className="flex flex-wrap h-full">
        <div className="flex justify-between items-center w-full">
          <div className="flex flex-col">
            <Header title={i18n.t('wisbitPage.index.createWisbit')} />
          </div>
        </div>
        <Loading />
      </div>
    );
  }

  return (
    <div className="flex flex-wrap h-full">
      <div className="flex justify-between items-center w-full">
        <div className="flex flex-col">
          {editorMode === MODE.CREATE && (
            <div className="flex items-center">
              <img src={ArrowLeft} alt="arrow-left" className="mr-2" />
              <LinkButton to={ROUTE.Wisbit.Template}>
                {i18n.t('wisbitPage.index.wisbitType')}
              </LinkButton>
            </div>
          )}
          <Header
            title={i18n.t('wisbitPage.index.createWisbit')}
            className="pb-8"
          />
        </div>
        <div className="flex items-center">
          {published && (
            <WarningMessage className="w-92">
              {i18n.t('wisbitPage.index.unchangeableWisbit')}
            </WarningMessage>
          )}
          {errorMessage(FIELDS.BITS) && (
            <ErrorMessage className="mr-2 w-92 h-20 overflow-auto">
              <div
                dangerouslySetInnerHTML={{
                  __html: errorMessage(FIELDS.BITS) || '',
                }}
              />
            </ErrorMessage>
          )}
          {editorMode !== MODE.VIEW && !published && (
            <>
              {showSavedMessage && (
                <ToastMessage className="mr-4">
                  {i18n.t('wisbitPage.index.wisbitSaved')}
                </ToastMessage>
              )}
              {id && (
                <>
                  <span>{i18n.t('wisbitPage.index.publish.title')}</span>
                  <div className="relative">
                    <ToggleButton
                      active={published}
                      onClick={onShowPublishMessage}
                      className="ml-2"
                    />
                    {showPublishMessage && (
                      <ConfirmMessage
                        className="h-40 w-84 right-0"
                        cancelText={i18n.t('wisbitPage.index.publish.cancel')}
                        onCancel={onShowPublishMessage}
                        confirmText={i18n.t('wisbitPage.index.publish.confirm')}
                        onConfirm={onPublish}
                      >
                        {i18n.t('wisbitPage.index.publish.prompt')}
                      </ConfirmMessage>
                    )}
                  </div>
                </>
              )}
            </>
          )}
          {/* Download results button */}
          {(published || editorMode === MODE.VIEW) && (
            <DownloadButton onClick={onDownload} />
          )}
          {/* Duplicate button */}
          {editorMode !== MODE.CREATE && isContentCreator && (
            <DuplicateButton onClick={onDuplicate} />
          )}
          {/* Delete button */}
          {editorMode !== MODE.CREATE && <DeleteButton onClick={onDelete} />}
        </div>
      </div>
      <div className="flex w-full h-full-24 overflow-auto">
        <GeneralInformation mode={editorMode} errorMessage={errorMessage} />
        <Bits mode={editorMode} />
      </div>
    </div>
  );
}

export default WisbitPage;
